/**
 * GTM's `window.dataLayer` — the transport every GA4 tag on the site reads.
 *
 * Rails bootstraps the array in `shared/_google_tag_consent` and loads the
 * container in `shared/_google_tag_manager`; this module only pushes onto it.
 *
 * Not to be confused with `Libs/DataLayer`, which is the react-query cache and
 * shares nothing with this but the word.
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type ITrackingParams = Record<string, unknown>;

/**
 * Push one event onto the GTM data layer.
 *
 * The push is dropped when no data layer exists: a layout that skips the GTM
 * partials (as `react_bare` did before HUB-5279) leaves it undefined, and
 * tracking must never be the reason a flow throws.
 *
 * User-level properties — `user_id`, `login_status`, `page_language`,
 * `platform`, the `user_subscription_*` set — are not repeated here. Rails
 * pushes them once per page load via `GoogleTagManager::Events::PageViewed`,
 * and GTM's data layer model keeps them readable by later tags.
 */
export const pushDataLayerEvent = (
  event: string,
  params: ITrackingParams = {}
): void => {
  if (typeof window === "undefined" || !window.dataLayer) return;

  window.dataLayer.push({ event, ...params });
};
